// @ts-nocheck
import axios, {AxiosInstance, AxiosRequestConfig} from 'axios';
import {globals} from '../../../../app/src/globals';
import {translate} from '../../plugins/i18n/i18n';
// Deep import: store/index.js eagerly loads Vuex form modules (Form TDZ cycle).
import {useNotificationsStore} from '../../store/pinia/notifications';

function getPostgrestProfile() {
  const contextProfile = globals?.$appContext?.postgrestProfile;
  if (typeof contextProfile === 'string' && contextProfile.trim().length > 0) {
    return contextProfile;
  }

  const tokenSchemaId = globals?.$keycloak?.tokenParsed?.schema_id;
  if (typeof tokenSchemaId === 'string' && tokenSchemaId.trim().length > 0) {
    return tokenSchemaId;
  }

  return undefined;
}

export class AxiosSingleton {
  private static _instance: AxiosSingleton;
  axios: AxiosInstance;

  private constructor() {
    this.axios = axios.create();

    this.axios.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        const token_is_valid =
          globals.$keycloak.token && !globals.$keycloak.isTokenExpired();
        if (token_is_valid) {
          config.headers['Authorization'] = `Bearer ${globals.$keycloak.token}`;
        } else {
          console.error(`Error : Session expired`);
          const msg = globals.$i18n.global.t(
            'hit-app.keycloak.session-expired-please-login',
          );
          if (confirm(msg)) {
            globals.$keycloak.logout();
          }
          throw new this.axios.Cancel(msg);
        }

        return config;
      },
      (error) => {
        // Gérer les erreurs de l'intercepteur de requête ici
        return Promise.reject(error);
      },
    );
  }

  public static get Instance() {
    return this._instance || (this._instance = new this());
  }
}

export type PostgRestRoute =
  | 'action'
  | 'action_history'
  | 'action_tag'
  | 'address'
  | 'address_communication_mode_data'
  | 'address_tag'
  | 'communication_mode'
  | 'config'
  | 'cost'
  | 'cost_data'
  | 'country'
  | 'document'
  | 'document_history'
  | 'qm_document_history'
  | 'equipment'
  | 'equipment_tag'
  | 'form'
  | 'form_definition'
  | 'journal'
  | 'journal_period_number'
  | 'notification'
  | 'period'
  | 'poi'
  | 'project'
  | 'project_part'
  | 'project_part_tag'
  | 'project_part_spec_sheet'
  | 'project_part_spec_sheet_history'
  | 'project_part_resource'
  | 'project_tag'
  | 'qualification'
  | 'resource'
  | 'resource_category'
  | 'resource_data'
  | 'resource_tag'
  | 'staff'
  | 'staff_compensation_type'
  | 'staff_compensation_tarification'
  | 'staff_compensation_value'
  | 'staff_compensation_day_validation'
  | 'staff_tag'
  | 'staff_qualification'
  | 'timeslot'
  | 'timeslot_day_validation'
  | 'tag'
  | 'tax_code'
  | 'transaction'
  | 'transaction_staff'
  | 'transaction_item'
  | 'unit'
  | 'unit_alias'
  | 'project_part_index'
  | 'standard_index'
  | 'project_part_item'
  | 'project_part_item_data'
  | 'project_part_bill_standard_index_value'
  | 'project_part_bill_index_value'
  | 'payment_schedule'
  | 'alert'
  | 'alert_definition'
  | 'alert_definition_timeslot'
  | 'alert_definition_equipment'
  | 'position'
  | 'event';

export class DataService {
  private static get baseUrl() {
    return process.env.VUE_APP_POSTGREST_URL;
  }
  private static get axios() {
    return AxiosSingleton.Instance.axios;
  }

  /**
   * Returns promise of the api call
   * @param route route of the model, with a slash at the beginning
   * @param attributes fields to query
   * @param filters dict with key value pairs where key is the field name and value is the filter to apply,
   * see <a href="https://postgrest.org/en/stable/api.html?highlight=attributes#horizontal-filtering-rows">Documentation</a>
   * @param order for example 'name.asc' or 'name.desc'
   * @param range for example '23-68'
   * @param getCount request to get the total number of items in the table from Postgrest
   * @param limit maximum number of elements, only used when range is omitted or invalid
   * @param offset offset from which the first element gets taken
   * @param config request configuration, see <a href="https://axios-http.com/docs/req_config">Documentation</a>
   */
  static read(
    route: PostgRestRoute,
    {
      attributes = '',
      filters = {} as Record<string, any>,
      order = '',
      range = '',
      getCount = false,
      limit = 0,
      offset = 0,
      config = {} as AxiosRequestConfig,
    },
  ) {
    const path = this.baseUrl + route;

    //TODO nigelapi implement/test correct escaping
    const params: {
      select?: string;
      [key: string]: any;
      order?: string;
      range?: string;
      limit?: number;
    } = {};
    if (attributes) params['select'] = attributes;
    if (order) params['order'] = order;
    if (limit !== 0) params['limit'] = limit;
    if (offset !== 0) params['offset'] = offset;

    if (range) {
      const rangeArray = range.split('-');
      const rangeLower = parseInt(rangeArray[0], 10);
      const rangeUpper = parseInt(rangeArray[1], 10);
      if (rangeArray.length < 2 || isNaN(rangeLower) || isNaN(rangeUpper)) {
        //TODO nigel check error handling, silent or visible?
        console.error('Invalid range');
        // throw new Error("Invalid range")
      } else {
        params['limit'] = rangeUpper - rangeLower;
        params['offset'] = rangeLower;
      }
    }

    if (Object.keys(filters).length > 0) {
      Object.keys(filters).forEach((key) => {
        if (key.endsWith(':or') && key.startsWith('or')) {
          params['or'] = filters[key];
        } else if (key.endsWith(':and') && key.startsWith('and')) {
          params['and'] = filters[key];
        } else {
          params[key] = filters[key];
        }
      });
    }

    config.params = params;
    if (!config.headers) {
      config.headers = {};
    }
    if (getCount) {
      config.headers['Prefer'] = 'count=exact'; //TODO check if count=planned should be used instead
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    const profile = getPostgrestProfile();
    if (profile) {
      config.headers['Accept-Profile'] = profile;
    }
    return this.axios.get(path, config);
  }

  //TODO nigelapi what to return?
  /**
   * Returns
   * @param route route of the model, with a slash at the beginning
   * @param input Object(s) to insert, dict for single object, list of dicts for multiple objects, must contain key value pairs for every column to fill
   * @param config request configuration, see <a href="https://axios-http.com/docs/req_config">Documentation</a>
   */
  static create(
    route: PostgRestRoute,
    input: Record<string, any> | [Record<string, any>],
    config: AxiosRequestConfig = {},
  ) {
    const path = this.baseUrl + route;
    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    const profile = getPostgrestProfile();
    if (profile) {
      config.headers['Content-Profile'] = profile;
    }
    return this.axios.post(path, input, config);
  }

  /**
   *
   * @param route route of the model, with a slash at the beginning
   * @param filters dict with key value pairs where key is the field name and value is the filter to apply,
   * see <a href="https://postgrest.org/en/stable/api.html?highlight=attributes#horizontal-filtering-rows">Documentation</a>
   * @param values key value pairs for every column to fill
   * @param config request configuration, see <a href="https://axios-http.com/docs/req_config">Documentation</a>
   */
  static update(
    route: PostgRestRoute,
    filters: Record<string, any>,
    values: Record<string, any>,
    config = {} as AxiosRequestConfig,
  ) {
    const path = this.baseUrl + route;
    config.params = filters;
    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    const profile = getPostgrestProfile();
    if (profile) {
      config.headers['Content-Profile'] = profile;
    }
    return this.axios
      .patch(path, values, config)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        if (error.response && error.response.status === 409) {
          useNotificationsStore().insertBannerNotification(
            translate('hit-base.common.error-update-409'),
          );
        } else {
          useNotificationsStore().insertBannerNotification(
            translate('hit-base.banners.unknown-error-title'),
          );
        }
      });
  }

  /**
   *
   * @param route route of the model, with a slash at the beginning
   * @param filters dict with key value pairs where key is the field name and value is the filter to apply,
   * see <a href="https://postgrest.org/en/stable/api.html?highlight=attributes#horizontal-filtering-rows">Documentation</a>
   * @param config request configuration, see <a href="https://axios-http.com/docs/req_config">Documentation</a>
   */
  static delete(
    route: PostgRestRoute,
    filters: Record<string, any>,
    config = {} as AxiosRequestConfig,
  ) {
    const path = this.baseUrl + route;
    config.params = filters;
    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    const profile = getPostgrestProfile();
    if (profile) {
      config.headers['Content-Profile'] = profile;
    }
    return this.axios
      .delete(path, config)
      .then((response) => {
        return response;
      })
      .catch((error) => {
        if (error.response.status === 409) {
          useNotificationsStore().insertBannerNotification(
            translate('hit-base.common.error-409'),
          );
          throw Error('Dataservice delete => 409');
        } else {
          console.error(error);
          throw Error(
            'An error happened during the delete call of the Dataservice',
          );
        }
      });
  }

  static procedure(
    route: string,
    data: Record<string, any>,
    {
      attributes = '',
      filters: _filters = {} as Record<string, any>,
      order = '',
      range: _range = '',
      getCount = false,
      limit = 0,
      offset = 0,
      config = {} as AxiosRequestConfig,
    } = {},
  ) {
    const path = this.baseUrl + 'rpc/' + route;
    if (!config.headers) {
      config.headers = {};
    }
    config.headers['Authorization'] = 'Bearer ' + globals.$keycloak.token;
    const profile = getPostgrestProfile();
    if (profile) {
      config.headers['Content-Profile'] = profile;
    }

    const params: {
      select?: string;
      [key: string]: any;
      order?: string;
      range?: string;
      limit?: number;
    } = {};
    if (attributes) params['select'] = attributes;
    if (order) params['order'] = order;
    if (limit !== 0) params['limit'] = limit;
    if (offset !== 0) params['offset'] = offset;
    config.params = params;

    if (getCount) {
      config.headers['Prefer'] = 'count=exact'; //TODO check if count=planned should be used instead
    }

    return this.axios.post(path, data, config);
  }

  //TODO nigelapi what to return?
  /**
   * Returns
   * @param route route of the model, with a slash at the beginning
   * @param input Object(s) to insert, dict for single object, list of dicts for multiple objects, must contain key value pairs for every column to fill
   */
  static upsert(
    route: PostgRestRoute,
    input: Record<string, any> | [Record<string, any>],
  ) {
    return this.create(route, input, {
      headers: {prefer: 'resolution=merge-duplicates'},
    });
  }

  /**
   * needed to escape strings in postgrest filters that contain the symbols .,:
   */
  static escape(str: string): string {
    return '%22' + str + '%22';
  }
}
